/**
 * Public legal pages (Zone 1): /terms and /privacy, linked from the marketing
 * footer. API-free — a prospect can read the Terms of Service (with the Data
 * Processing Addendum it incorporates) and the Privacy Policy without
 * entering the signup wizard.
 *
 * Each page renders exactly one H1; the shared LegalDocumentView is told to
 * suppress its own document title (`titleAs="none"`), except the DPA on
 * /terms which renders as an H2 document break so readers can see where the
 * Terms end and the addendum begins.
 */
import { Link } from 'react-router-dom';

import { LegalDocumentView, sectionAnchorId } from '../../components/legal-document-view';
import { ROUTES } from '../../lib/constants';
import {
  DPA_DOCUMENT,
  PRIVACY_DOCUMENT,
  TOS_DOCUMENT,
  type LegalDocument,
} from '../../lib/legal-copy';

import { MarketingChrome } from './marketing-chrome';

/** The two documents a prospect reads at /terms, with their anchor prefixes. */
const TERMS_DOCUMENTS: ReadonlyArray<{ document: LegalDocument; prefix: string }> = [
  { document: TOS_DOCUMENT, prefix: 'tos' },
  { document: DPA_DOCUMENT, prefix: 'dpa' },
];

/** Sticky sidebar TOC for /terms (desktop only; hidden by CSS below 1024px). */
function TermsToc(): JSX.Element {
  return (
    <nav className="legal-page__toc" aria-label="On this page">
      <span className="legal-page__toc-title">On this page</span>
      {TERMS_DOCUMENTS.map(({ document, prefix }) => (
        <div className="legal-page__toc-group" key={prefix}>
          <span className="legal-page__toc-doc">{document.title}</span>
          <ul>
            {document.sections.map((section) => (
              <li key={section.heading}>
                <a href={`#${sectionAnchorId(prefix, section.heading)}`}>{section.heading}</a>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function TermsPage(): JSX.Element {
  return (
    <MarketingChrome>
      <div className="legal-page legal-page--with-toc">
        <header className="legal-page__header">
          <h1>Terms of Service</h1>
          <p className="legal-page__lede">
            The version you accept during signup is authoritative and is recorded with your consent.
            See also the <Link to={ROUTES.privacy}>Privacy Policy</Link>.
          </p>
        </header>
        <div className="legal-page__layout">
          <TermsToc />
          <div className="legal-page__body">
            <LegalDocumentView document={TOS_DOCUMENT} titleAs="none" anchorPrefix="tos" />
            {/* The DPA forms part of the Terms of Service (its own intro says
                so); the H2 document break marks the boundary between the two
                documents, whose sections both number from 1. */}
            <LegalDocumentView document={DPA_DOCUMENT} titleAs="h2" anchorPrefix="dpa" />
          </div>
        </div>
      </div>
    </MarketingChrome>
  );
}

export function PrivacyPage(): JSX.Element {
  return (
    <MarketingChrome>
      <div className="legal-page">
        <header className="legal-page__header">
          <h1>Privacy Policy</h1>
          <p className="legal-page__lede">
            How program data is handled contractually is set out in the{' '}
            <Link to={ROUTES.terms}>Terms of Service</Link> and its Data Processing Addendum.
          </p>
        </header>
        <div className="legal-page__body">
          <LegalDocumentView document={PRIVACY_DOCUMENT} titleAs="none" anchorPrefix="privacy" />
        </div>
      </div>
    </MarketingChrome>
  );
}
